export interface NumberItem {
  number: number;
  word: string;
  native: string;
  phonetic?: string;
  image: string;
}

export type LanguageCode = 'en' | 'bn' | 'ar' | 'es' | 'fr' | 'hi' | 'ko' | 'zh' | 'ja';

export const languageNames: Record<LanguageCode, string> = {
  en: 'English',
  bn: 'Bangla',
  ar: 'Arabic',
  es: 'Spanish',
  fr: 'French',
  hi: 'Hindi',
  ko: 'Korean',
  zh: 'Chinese',
  ja: 'Japanese'
};

export const bcp47Tags: Record<LanguageCode, string> = {
  en: 'en-US',
  bn: 'bn-BD',
  ar: 'ar-SA',
  es: 'es-ES',
  fr: 'fr-FR',
  hi: 'hi-IN',
  ko: 'ko-KR',
  zh: 'zh-CN',
  ja: 'ja-JP'
};

export const numbersData: Record<LanguageCode, NumberItem[]> = {
  en: [
    { number: 0, word: 'Zero', native: 'Zero', image: '⭕' },
    { number: 1, word: 'One', native: 'One', image: '🍎' },
    { number: 2, word: 'Two', native: 'Two', image: '🍐' },
    { number: 3, word: 'Three', native: 'Three', image: '🍊' },
    { number: 4, word: 'Four', native: 'Four', image: '🍋' },
    { number: 5, word: 'Five', native: 'Five', image: '🍌' },
    { number: 6, word: 'Six', native: 'Six', image: '🍉' },
    { number: 7, word: 'Seven', native: 'Seven', image: '🍇' },
    { number: 8, word: 'Eight', native: 'Eight', image: '🍓' },
    { number: 9, word: 'Nine', native: 'Nine', image: '🫐' },
    { number: 10, word: 'Ten', native: 'Ten', image: '🍒' },
    { number: 11, word: 'Eleven', native: 'Eleven', image: '🥭' },
    { number: 12, word: 'Twelve', native: 'Twelve', image: '🍍' },
    { number: 13, word: 'Thirteen', native: 'Thirteen', image: '🥝' },
    { number: 14, word: 'Fourteen', native: 'Fourteen', image: '🍅' },
    { number: 15, word: 'Fifteen', native: 'Fifteen', image: '🍆' },
    { number: 16, word: 'Sixteen', native: 'Sixteen', image: '🌽' },
    { number: 17, word: 'Seventeen', native: 'Seventeen', image: '🥕' },
    { number: 18, word: 'Eighteen', native: 'Eighteen', image: '🥔' },
    { number: 19, word: 'Nineteen', native: 'Nineteen', image: '🍠' },
    { number: 20, word: 'Twenty', native: 'Twenty', image: '🥑' },
  ],
  bn: [
    { number: 0, word: 'Shunno', native: 'শূন্য', image: '⭕' },
    { number: 1, word: 'Ek', native: 'এক', image: '🍎' },
    { number: 2, word: 'Dui', native: 'দুই', image: '🍐' },
    { number: 3, word: 'Tin', native: 'তিন', image: '🍊' },
    { number: 4, word: 'Char', native: 'চার', image: '🍋' },
    { number: 5, word: 'Paanch', native: 'পাঁচ', image: '🍌' },
    { number: 6, word: 'Chhoy', native: 'ছয়', image: '🍉' },
    { number: 7, word: 'Saat', native: 'সাত', image: '🍇' },
    { number: 8, word: 'Aat', native: 'আট', image: '🍓' },
    { number: 9, word: 'Noy', native: 'নয়', image: '🫐' },
    { number: 10, word: 'Dosh', native: 'দশ', image: '🍒' },
    { number: 11, word: 'Ekaro', native: 'এগারো', image: '🥭' },
    { number: 12, word: 'Baro', native: 'বারো', image: '🍍' },
    { number: 13, word: 'Tero', native: 'তেরো', image: '🥝' },
    { number: 14, word: 'Choddo', native: 'চৌদ্দ', image: '🍅' },
    { number: 15, word: 'Poro', native: 'পনেরো', image: '🍆' },
    { number: 16, word: 'Sholo', native: 'ষোলো', image: '🌽' },
    { number: 17, word: 'Shotoro', native: 'সতেরো', image: '🥕' },
    { number: 18, word: 'Atahro', native: 'আঠারো', image: '🥔' },
    { number: 19, word: 'Unish', native: 'উনিশ', image: '🍠' },
    { number: 20, word: 'Bish', native: 'বিশ', image: '🥑' },
  ],
  ar: [
    { number: 0, word: 'Sifr', native: 'صفر', image: '⭕' },
    { number: 1, word: 'Wahid', native: 'واحد', image: '🍎' },
    { number: 2, word: 'Ithnan', native: 'اثنان', image: '🍐' },
    { number: 3, word: 'Thalatha', native: 'ثلاثة', image: '🍊' },
    { number: 4, word: 'Arba\'a', native: 'أربعة', image: '🍋' },
    { number: 5, word: 'Khamsa', native: 'خمسة', image: '🍌' },
    { number: 6, word: 'Sitta', native: 'ستة', image: '🍉' },
    { number: 7, word: 'Sab\'a', native: 'سبعة', image: '🍇' },
    { number: 8, word: 'Thamaniya', native: 'ثمانية', image: '🍓' },
    { number: 9, word: 'Tis\'a', native: 'تسعة', image: '🫐' },
    { number: 10, word: 'Ashara', native: 'عشرة', image: '🍒' },
    { number: 11, word: 'Ahada Ashar', native: 'أحد عشر', image: '🥭' },
    { number: 12, word: 'Ithna Ashar', native: 'اثنا عشر', image: '🍍' },
    { number: 13, word: 'Thalathata Ashar', native: 'ثلاثة عشر', image: '🥝' },
    { number: 14, word: 'Arba\'ata Ashar', native: 'أربعة عشر', image: '🍅' },
    { number: 15, word: 'Khamsata Ashar', native: 'خمسة عشر', image: '🍆' },
    { number: 16, word: 'Sittata Ashar', native: 'ستة عشر', image: '🌽' },
    { number: 17, word: 'Sab\'ata Ashar', native: 'سبعة عشر', image: '🥕' },
    { number: 18, word: 'Thamaniyata Ashar', native: 'ثمانية عشر', image: '🥔' },
    { number: 19, word: 'Tis\'ata Ashar', native: 'تسعة عشر', image: '🍠' },
    { number: 20, word: 'Ishrun', native: 'عشرون', image: '🥑' },
  ],
  es: [
    { number: 0, word: 'Cero', native: 'Cero', image: '⭕' },
    { number: 1, word: 'Uno', native: 'Uno', image: '🍎' },
    { number: 2, word: 'Dos', native: 'Dos', image: '🍐' },
    { number: 3, word: 'Tres', native: 'Tres', image: '🍊' },
    { number: 4, word: 'Cuatro', native: 'Cuatro', image: '🍋' },
    { number: 5, word: 'Cinco', native: 'Cinco', image: '🍌' },
    { number: 6, word: 'Seis', native: 'Seis', image: '🍉' },
    { number: 7, word: 'Siete', native: 'Siete', image: '🍇' },
    { number: 8, word: 'Ocho', native: 'Ocho', image: '🍓' },
    { number: 9, word: 'Nueve', native: 'Nueve', image: '🫐' },
    { number: 10, word: 'Diez', native: 'Diez', image: '🍒' },
    { number: 11, word: 'Once', native: 'Once', image: '🥭' },
    { number: 12, word: 'Doce', native: 'Doce', image: '🍍' },
    { number: 13, word: 'Trece', native: 'Trece', image: '🥝' },
    { number: 14, word: 'Catorce', native: 'Catorce', image: '🍅' },
    { number: 15, word: 'Quince', native: 'Quince', image: '🍆' },
    { number: 16, word: 'Dieciséis', native: 'Dieciséis', image: '🌽' },
    { number: 17, word: 'Diecisiete', native: 'Diecisiete', image: '🥕' },
    { number: 18, word: 'Dieciocho', native: 'Dieciocho', image: '🥔' },
    { number: 19, word: 'Diecinueve', native: 'Diecinueve', image: '🍠' },
    { number: 20, word: 'Veinte', native: 'Veinte', image: '🥑' },
  ],
  fr: [
    { number: 0, word: 'Zéro', native: 'Zéro', image: '⭕' },
    { number: 1, word: 'Un', native: 'Un', image: '🍎' },
    { number: 2, word: 'Deux', native: 'Deux', image: '🍐' },
    { number: 3, word: 'Trois', native: 'Trois', image: '🍊' },
    { number: 4, word: 'Quatre', native: 'Quatre', image: '🍋' },
    { number: 5, word: 'Cinq', native: 'Cinq', image: '🍌' },
    { number: 6, word: 'Six', native: 'Six', image: '🍉' },
    { number: 7, word: 'Sept', native: 'Sept', image: '🍇' },
    { number: 8, word: 'Huit', native: 'Huit', image: '🍓' },
    { number: 9, word: 'Neuf', native: 'Neuf', image: '🫐' },
    { number: 10, word: 'Dix', native: 'Dix', image: '🍒' },
    { number: 11, word: 'Onze', native: 'Onze', image: '🥭' },
    { number: 12, word: 'Douze', native: 'Douze', image: '🍍' },
    { number: 13, word: 'Treize', native: 'Treize', image: '🥝' },
    { number: 14, word: 'Quatorze', native: 'Quatorze', image: '🍅' },
    { number: 15, word: 'Quinze', native: 'Quinze', image: '🍆' },
    { number: 16, word: 'Seize', native: 'Seize', image: '🌽' },
    { number: 17, word: 'Dix-sept', native: 'Dix-sept', image: '🥕' },
    { number: 18, word: 'Dix-huit', native: 'Dix-huit', image: '🥔' },
    { number: 19, word: 'Dix-neuf', native: 'Dix-neuf', image: '🍠' },
    { number: 20, word: 'Vingt', native: 'Vingt', image: '🥑' },
  ],
  hi: [
    { number: 0, word: 'Shunya', native: 'शून्य', image: '⭕' },
    { number: 1, word: 'Ek', native: 'एक', image: '🍎' },
    { number: 2, word: 'Do', native: 'दो', image: '🍐' },
    { number: 3, word: 'Teen', native: 'तीन', image: '🍊' },
    { number: 4, word: 'Chaar', native: 'चार', image: '🍋' },
    { number: 5, word: 'Paanch', native: 'पाँच', image: '🍌' },
    { number: 6, word: 'Chhah', native: 'छह', image: '🍉' },
    { number: 7, word: 'Saat', native: 'सात', image: '🍇' },
    { number: 8, word: 'Aath', native: 'आठ', image: '🍓' },
    { number: 9, word: 'Nau', native: 'नौ', image: '🫐' },
    { number: 10, word: 'Das', native: 'दस', image: '🍒' },
    { number: 11, word: 'Gyarah', native: 'ग्यारह', image: '🥭' },
    { number: 12, word: 'Barah', native: 'बारह', image: '🍍' },
    { number: 13, word: 'Terah', native: 'तेरह', image: '🥝' },
    { number: 14, word: 'Chaudah', native: 'चौदह', image: '🍅' },
    { number: 15, word: 'Pandrah', native: 'पंद्रह', image: '🍆' },
    { number: 16, word: 'Solah', native: 'सोलह', image: '🌽' },
    { number: 17, word: 'Satrah', native: 'सत्रह', image: '🥕' },
    { number: 18, word: 'Atharah', native: 'अठारह', image: '🥔' },
    { number: 19, word: 'Unnees', native: 'उन्नीस', image: '🍠' },
    { number: 20, word: 'Bees', native: 'बीस', image: '🥑' },
  ],
  ko: [
    { number: 0, word: 'Yeong', native: '영', image: '⭕' },
    { number: 1, word: 'Il', native: '일', image: '🍎' },
    { number: 2, word: 'I', native: '이', image: '🍐' },
    { number: 3, word: 'Sam', native: '삼', image: '🍊' },
    { number: 4, word: 'Sa', native: '사', image: '🍋' },
    { number: 5, word: 'O', native: '오', image: '🍌' },
    { number: 6, word: 'Yuk', native: '육', image: '🍉' },
    { number: 7, word: 'Chil', native: '칠', image: '🍇' },
    { number: 8, word: 'Pal', native: '팔', image: '🍓' },
    { number: 9, word: 'Gu', native: '구', image: '🫐' },
    { number: 10, word: 'Sip', native: '십', image: '🍒' },
    { number: 11, word: 'Sip-il', native: '십일', image: '🥭' },
    { number: 12, word: 'Sip-i', native: '십이', image: '🍍' },
    { number: 13, word: 'Sip-sam', native: '십삼', image: '🥝' },
    { number: 14, word: 'Sip-sa', native: '십사', image: '🍅' },
    { number: 15, word: 'Sip-o', native: '십오', image: '🍆' },
    { number: 16, word: 'Sip-yuk', native: '십육', image: '🌽' },
    { number: 17, word: 'Sip-chil', native: '십칠', image: '🥕' },
    { number: 18, word: 'Sip-pal', native: '십팔', image: '🥔' },
    { number: 19, word: 'Sip-gu', native: '십구', image: '🍠' },
    { number: 20, word: 'I-sip', native: '이십', image: '🥑' },
  ],
  zh: [
    { number: 0, word: 'Líng', native: '零', image: '⭕' },
    { number: 1, word: 'Yī', native: '一', image: '🍎' },
    { number: 2, word: 'Èr', native: '二', image: '🍐' },
    { number: 3, word: 'Sān', native: '三', image: '🍊' },
    { number: 4, word: 'Sì', native: '四', image: '🍋' },
    { number: 5, word: 'Wǔ', native: '五', image: '🍌' },
    { number: 6, word: 'Liù', native: '六', image: '🍉' },
    { number: 7, word: 'Qī', native: '七', image: '🍇' },
    { number: 8, word: 'Bā', native: '八', image: '🍓' },
    { number: 9, word: 'Jiǔ', native: '九', image: '🫐' },
    { number: 10, word: 'Shí', native: '十', image: '🍒' },
    { number: 11, word: 'Shíyī', native: '十一', image: '🥭' },
    { number: 12, word: 'Shíèr', native: '十二', image: '🍍' },
    { number: 13, word: 'Shísān', native: '十三', image: '🥝' },
    { number: 14, word: 'Shísì', native: '十四', image: '🍅' },
    { number: 15, word: 'Shíwǔ', native: '十五', image: '🍆' },
    { number: 16, word: 'Shíliù', native: '十六', image: '🌽' },
    { number: 17, word: 'Shíqī', native: '十七', image: '🥕' },
    { number: 18, word: 'Shíbā', native: '十八', image: '🥔' },
    { number: 19, word: 'Shíjiǔ', native: '十九', image: '🍠' },
    { number: 20, word: 'Èrshí', native: '二十', image: '🥑' },
  ],
  ja: [
    { number: 0, word: 'Zero', native: 'ゼロ', image: '⭕' },
    { number: 1, word: 'Ichi', native: '一', image: '🍎' },
    { number: 2, word: 'Ni', native: '二', image: '🍐' },
    { number: 3, word: 'San', native: '三', image: '🍊' },
    { number: 4, word: 'Yon', native: '四', image: '🍋' },
    { number: 5, word: 'Go', native: '五', image: '🍌' },
    { number: 6, word: 'Roku', native: '六', image: '🍉' },
    { number: 7, word: 'Nana', native: '七', image: '🍇' },
    { number: 8, word: 'Hachi', native: '八', image: '🍓' },
    { number: 9, word: 'Kyuu', native: '九', image: '🫐' },
    { number: 10, word: 'Juu', native: '十', image: '🍒' },
    { number: 11, word: 'Juu-ichi', native: '十一', image: '🥭' },
    { number: 12, word: 'Juu-ni', native: '十二', image: '🍍' },
    { number: 13, word: 'Juu-san', native: '十三', image: '🥝' },
    { number: 14, word: 'Juu-yon', native: '十四', image: '🍅' },
    { number: 15, word: 'Juu-go', native: '十五', image: '🍆' },
    { number: 16, word: 'Juu-roku', native: '十六', image: '🌽' },
    { number: 17, word: 'Juu-nana', native: '十七', image: '🥕' },
    { number: 18, word: 'Juu-hachi', native: '十八', image: '🥔' },
    { number: 19, word: 'Juu-kyuu', native: '十九', image: '🍠' },
    { number: 20, word: 'Ni-juu', native: '二十', image: '🥑' },
  ],
};
